import { EducationIcon } from "assets/icons/DetailedProfile";
import React, { useContext, useEffect, useState } from "react";
import { SectionTitle } from "../../Shared";
import { SingleEducation, EducationComment } from "./Index";
import { Button } from "antd";
// import editPencil from 'assets/icons/profileIcons/editPencil.svg';
import DELETE_ICON from "assets/icons/DetailedProfile/delete.svg";
import { PlusOutlined } from "@ant-design/icons";
import { EditContext } from "../../Context/EditContext";
import _ from "lodash";

interface PropsType {
  candidate: any;
  nocomments?: any;
  isRecruiterPage?: boolean;
  handleUpdate?: any;
  handleDeleteItem?: any;
  setDisplayEducationForm?: any;
  isEditing?: any;
}

const Education: React.FC<PropsType> = ({
  candidate,
  nocomments,
  isRecruiterPage,
  handleUpdate,
  handleDeleteItem,
  setDisplayEducationForm,
  isEditing,
}) => {
  const [edu, setEdu] = useState([]);
  const [data, setData] = useState([]);
  const editContext = useContext<any>(EditContext);

  // Normalize education data without mutating the prop
  const normalizedEducation = React.useMemo(() => {
    if (!candidate) return [];

    // Get education array (from educations for recruiter page, or education for candidate page)
    let educationArray = isRecruiterPage
      ? candidate?.educations || candidate?.education || []
      : candidate?.education || [];

    // Clone to avoid mutating the original
    educationArray = _.cloneDeep(educationArray);

    // Sort if not in edit mode
    if (!editContext?.edit && educationArray.length > 0) {
      educationArray.sort((a: any, b: any): any => {
        if (a.current && !b.current) {
          return -1; // a comes before b
        } else if (!a.current && b.current) {
          return 1; // b comes before a
        } else if (a.current && b.current) {
          return b.endYear - a.endYear; // sort by descending endYear
        } else {
          return b.endYear - a.endYear; // sort by descending endYear
        }
      });
    }

    return educationArray;
  }, [
    candidate?.educations,
    candidate?.education,
    isRecruiterPage,
    editContext?.edit,
  ]);

  const Styles = {
    root: {
      padding: isRecruiterPage ? 0 : 15,
      boxShadow: isRecruiterPage ? "unset" : "0px 0px 4px #00000040 ",
      borderRadius: 10,
      paddingBottom: 5,
    },
    body: {
      marginTop: isRecruiterPage ? 32 : 20,
    },
  };

  function mergeAndSort(arr: any) {
    if (!arr || arr.length === 0) return [];

    // Clone the array to avoid mutating the original
    const clonedArr = _.cloneDeep(arr);

    // Group entries by schoolName (similar to Experience grouping by company)
    const groupedEducations: any = {};
    const ungroupedEducations: any = [];

    for (let i = 0; i < clonedArr?.length; i++) {
      const education = clonedArr[i];
      const schoolName = education?.schoolName?.toLowerCase() || null;
      const hasTempId = education?._id?.includes(".");

      // Don't group entries with temporary IDs (standalone new entries from main "Add" button)
      if (hasTempId || !schoolName) {
        ungroupedEducations.push(education);
      } else {
        if (!groupedEducations[schoolName]) {
          groupedEducations[schoolName] = [];
        }
        groupedEducations[schoolName].push(education);
      }
    }

    const result: any = [];

    // Process grouped educations
    Object.keys(groupedEducations).forEach((schoolName) => {
      const group = groupedEducations[schoolName];
      if (group.length > 1) {
        // Create merged education with the first education as base
        const mergedEducation = { ...group[0] };
        mergedEducation.mergedData = [...group];

        // Sort merged educations by start/end year (only in view mode)
        if (!editContext?.edit) {
          mergedEducation.mergedData.sort((a: any, b: any) => {
            if (a.startYear !== b.startYear) {
              return b.startYear - a.startYear;
            } else {
              return b.endYear - a.endYear;
            }
          });
        }

        result.push(mergedEducation);
      } else {
        // Single education in group, add as-is
        result.push(group[0]);
      }
    });

    // Add ungrouped educations
    result.push(...ungroupedEducations);

    return result;
  }

  // Update edu state when normalizedEducation changes
  useEffect(() => {
    if (normalizedEducation) {
      setEdu(_.cloneDeep(normalizedEducation));
    } else {
      setEdu([]);
    }
  }, [normalizedEducation]);

  // Update data when edu changes
  useEffect(() => {
    if (edu && edu.length >= 0) {
      setData(mergeAndSort(edu));
    } else {
      setData([]);
    }
  }, [edu, editContext?.edit]);

  const handleClick = (education: any) => {
    handleUpdate({ ...education });
    setDisplayEducationForm(true);
  };

  const handleDelete = (edu: any) => {
    handleDeleteItem(edu);
  };

  const addNew = () => {
    // Get current education from updatedData or candidate, prioritizing updatedData
    // Use the raw education array (not merged data) to ensure we're working with individual entries
    let currentEducation =
      editContext?.updatedData?.educations ||
      editContext?.updatedData?.education ||
      editContext?.candidate?.educations ||
      editContext?.candidate?.education ||
      [];

    // Ensure we're working with a flat array (not merged data)
    // If entries have mergedData, extract all individual entries
    const flatEducation = currentEducation.flatMap((edu: any) => {
      if (
        edu.mergedData &&
        Array.isArray(edu.mergedData) &&
        edu.mergedData.length > 0
      ) {
        // Return all entries from mergedData
        return edu.mergedData.map((e: any) => ({ ...e }));
      }
      // Return the entry itself (clone to avoid mutation)
      return [{ ...edu }];
    });

    // Create a new education entry with a unique temporary ID (with dot to mark as temporary)
    // This ensures it won't merge with existing entries
    const newEducation = {
      _id: `temp.${Date.now()}.${Math.random().toString()}`,
      schoolName: null,
      degree: null,
      startYear: null,
      endYear: null,
      current: false,
      location: null,
      logo: null,
    };

    const edu = [...flatEducation, newEducation];

    editContext?.setUpdatedData({
      ...editContext?.updatedData,
      education: edu,
      educations: edu, // Also set educations for recruiter pages
    });
    editContext?.setData({
      ...editContext?.candidate,
      education: edu,
      educations: edu, // Also set educations for recruiter pages
    });
  };

  const hasEducation = data && data.length > 0;
  const showEmptyMessage = !hasEducation && !editContext?.edit;

  return (
    <>
      <div style={Styles.root}>
        <SectionTitle
          title="Education"
          icon={EducationIcon}
          fontSize="16px"
          showstyle={false}
        />
        <div style={Styles.body}>
          {hasEducation ? (
            data.map((education: any, i: any) => {
              return (
                <div
                  key={i}
                  style={{
                    marginBottom: "14px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <SingleEducation
                      education={education}
                      candidateId={candidate?._id}
                    />
                    {nocomments === false ? (
                      <></>
                    ) : (
                      (candidate?.educationComment || editContext?.edit) &&
                      i + 1 === data?.length && (
                        <EducationComment
                          comment={candidate?.educationComment}
                        />
                      )
                    )}
                  </div>
                  {/* Hide buttons when in edit mode as EducationForm already has delete button */}
                  {isRecruiterPage &&
                    isEditing &&
                    !editContext?.edit &&
                    handleUpdate &&
                    handleDeleteItem && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "start",
                          marginLeft: "8px",
                          gap: 4,
                          marginRight: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          <Button
                            style={{
                              zIndex: "5",
                              position: "relative",
                              backgroundColor: "#1A838C",
                              width: "20px",
                              minWidth: "20px",
                              height: "20px",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            className="border-primary"
                            shape="circle"
                            icon={
                              <img
                                style={{
                                  height: "9.5px",
                                  width: "9.5px",
                                }}
                                src={"editPencil"}
                                alt="editPencil"
                              />
                            }
                            onClick={() => handleClick(education)}
                          />
                          <span
                            onClick={() => handleDelete(education)}
                            style={{
                              cursor: "pointer",
                              display: "inline-block",
                            }}
                          >
                            <img
                              src={DELETE_ICON}
                              alt="DELETE_ICON"
                              style={{ height: "20px", width: "20px" }}
                            />
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              );
            })
          ) : showEmptyMessage ? (
            <div style={{ color: "#8c8c8c", fontStyle: "italic" }}>
              No education added
            </div>
          ) : null}
          {editContext?.edit && (
            <Button
              style={{
                maxWidth: "100%",
                marginBottom: "8px",
                borderRadius: "15px",
              }}
              type="dashed"
              onClick={addNew}
              block
            >
              Add a new education <PlusOutlined />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default Education;
